/**
 * Unterabschnitt „Antragskonferenz“ in den Stammdaten eines Antrags (ADR 011). Immer sichtbar,
 * damit die Konferenz nicht vergessen wird, aber ruhig:
 *
 * - **vor der Konferenz** (Status „Konferenz ausstehend“): der geplante Termin – die Frist des
 *   Status – und der Knopf „Antragskonferenz erfassen“, derselbe Übergang wie im Kopf
 * - **danach**: geplanter Termin, „Durchgeführt am“ und das Protokoll. Ist „Durchgeführt am“
 *   leer, wurde ohne Konferenz weitergeschaltet.
 *
 * Gespeichert wird ohne Knopf wie überall in den Stammdaten (ADR 004). Die Dokumente der
 * Konferenz sind die Dokumente am Antrag mit dem Status „Konferenz ausstehend“; die übrigen
 * bleiben beim Ändern unberührt.
 */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import Button from "../../../common/inputs/Button";
import DokumentListe from "../../../common/inputs/DokumentListe";
import InputFieldDate from "../../../common/inputs/InputFieldDate";
import SubSection, { subSectionFullWidth } from "../../../layout/SubSection";
import { colors } from "../../../../styles/theme";
import {
  PermitProcedure,
  PermitProcedureUpdate,
  ProcedureAction,
  ProcedureDocument,
} from "../../../../types";

interface AntragKonferenzProps {
  procedure: PermitProcedure;
  canEdit: boolean;
  onFieldChange: (data: PermitProcedureUpdate) => void;
  /** Öffnet den Dialog zu einer Aktion am Antrag */
  onAction: (action: ProcedureAction) => void;
}

const KONFERENZ_STATUS = "conference_pending";

const AntragKonferenz: React.FC<AntragKonferenzProps> = ({
  procedure,
  canEdit,
  onFieldChange,
  onAction,
}) => {
  const [planned, setPlanned] = useState(procedure.conference_planned_on ?? "");
  const [held, setHeld] = useState(procedure.conference_held_on ?? "");
  useEffect(
    () => setPlanned(procedure.conference_planned_on ?? ""),
    [procedure.conference_planned_on],
  );
  useEffect(
    () => setHeld(procedure.conference_held_on ?? ""),
    [procedure.conference_held_on],
  );

  const pending = procedure.status === KONFERENZ_STATUS;
  const record = procedure.actions.find(
    (action) => action.action === "record_conference",
  );
  const documents = procedure.documents.filter(
    (document) => document.status === KONFERENZ_STATUS,
  );

  const saveDocuments = (list: ProcedureDocument[]) =>
    onFieldChange({
      documents: [
        ...procedure.documents.filter(
          (document) => document.status !== KONFERENZ_STATUS,
        ),
        ...list.map((document) => ({ ...document, status: KONFERENZ_STATUS })),
      ] as ProcedureDocument[],
    });

  return (
    <SubSection
      title="Antragskonferenz"
      expandable={false}
      columns={2}
      sx={{ mt: 3, mb: 0 }}
      actions={
        pending && canEdit && record ? (
          <Button variant="secondary" onClick={() => onAction(record)}>
            {record.label}
          </Button>
        ) : undefined
      }
    >
      <InputFieldDate
        variant="floating"
        label="Antragskonferenz am"
        value={planned}
        onChange={(value) => setPlanned(value ?? "")}
        onCommit={(value) => onFieldChange({ conference_planned_on: value })}
        readOnly={!canEdit}
        tooltip="Der geplante Termin der Antragskonferenz mit Genehmigungs- und Fachbehörden. Er erscheint in den Terminen, bis die Konferenz erfasst ist."
        labelBackgroundColor={colors.background.paper}
      />
      {!pending && (
        <InputFieldDate
          variant="floating"
          label="Durchgeführt am"
          value={held}
          onChange={(value) => setHeld(value ?? "")}
          onCommit={(value) => onFieldChange({ conference_held_on: value })}
          readOnly={!canEdit}
          tooltip="Wann die Antragskonferenz stattfand. Leer heißt: Der Antrag wurde ohne Antragskonferenz weitergeschaltet."
          labelBackgroundColor={colors.background.paper}
        />
      )}
      {!pending && (
        <Box sx={subSectionFullWidth}>
          <DokumentListe
            label="Protokoll"
            dokumente={documents}
            onChange={saveDocuments}
            readOnly={!canEdit}
          />
        </Box>
      )}
    </SubSection>
  );
};

export default AntragKonferenz;
